// EmojiPicker — lightweight categorized emoji panel (no external deps).
// Used for the extended reaction selection and the desktop compose picker.
//
// Renders a full-screen backdrop + a positioned panel. `position` is a
// style object ({ top/left/bottom/right }) for the panel. onPick(emoji)
// fires on selection; onClose dismisses.
import { useState } from "react";
import { T_NEW } from "../theme.js";

const CATS = [
  { key: "recent", icon: "🕘", emojis: "👍 ❤️ 👎 🔥 🥰 👏 😁 😂 🙏 🎉 😍 😭".split(" ") },
  { key: "smileys", icon: "😀", emojis: "😀 😃 😄 😁 😆 😅 😂 🤣 🥲 ☺️ 😊 😇 🙂 🙃 😉 😌 😍 🥰 😘 😗 😙 😚 😋 😛 😝 😜 🤪 🤨 🧐 🤓 😎 🥸 🤩 🥳 😏 😒 😞 😔 😟 😕 🙁 ☹️ 😣 😖 😫 😩 🥺 😢 😭 😤 😠 😡 🤬 🤯 😳 🥵 🥶 😱 😨 😰 😥 😓 🤗 🤔 🤭 🫢 🤫 😶 😐 😑 😬 🙄 😯 😦 😧 😮 😲 🥱 😴 🤤 😪 😵 🤐 🥴 🤢 🤮 🤧 😷 🤒 🤕 🤑 🤠 😈 👿 👻 💀 ☠️ 👽 🤖 🎃".split(" ") },
  { key: "gestures", icon: "👍", emojis: "👍 👎 👌 🤌 🤏 ✌️ 🤞 🫰 🤟 🤘 🤙 👈 👉 👆 👇 ☝️ ✋ 🤚 🖐️ 🖖 👋 🤝 🙏 ✍️ 💅 🤳 💪 👏 🙌 👐 🤲 🫶 🙆 🙅 💁 🙋 🤦 🤷 🧏".split(" ") },
  { key: "hearts", icon: "❤️", emojis: "❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 💟 ❤️‍🔥 ❤️‍🩹 💌 💋 ⭐ 🌟 ✨ 💥 🔥 💯 ✅ ❌".split(" ") },
  { key: "animals", icon: "🐶", emojis: "🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐷 🐸 🐵 🐔 🐧 🐦 🐤 🦆 🦅 🦉 🐺 🐗 🐴 🦄 🐝 🐛 🦋 🐌 🐞 🐢 🐍 🐙 🦀 🐠 🐟 🐬 🐳 🐋 🦈 🐊 🐅 🐆 🦓 🦍 🐘 🦛 🐪 🐫 🦒 🦘".split(" ") },
  { key: "food", icon: "🍔", emojis: "🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🫐 🍈 🍒 🍑 🥭 🍍 🥥 🥝 🍅 🥑 🥦 🥕 🌽 🌶️ 🥔 🍠 🥐 🍞 🥖 🧀 🥚 🍳 🥞 🧇 🥓 🍔 🍟 🍕 🌭 🥪 🌮 🌯 🥙 🍜 🍝 🍣 🍱 🍩 🍪 🎂 🍰 🍫 🍬 🍭 🍦 ☕ 🍵 🍺 🍷 🥂".split(" ") },
  { key: "activity", icon: "⚽", emojis: "⚽ 🏀 🏈 ⚾ 🥎 🎾 🏐 🏉 🥏 🎱 🏓 🏸 🥊 🥋 ⛳ 🏹 🎣 🤿 🥌 🎿 🛷 🏂 🏄 🏊 🚴 🚵 🏆 🥇 🥈 🥉 🏅 🎖️ 🎯 🎮 🎲 🎸 🎹 🎺 🎻 🥁 🎤 🎧 🎬 🎨".split(" ") },
  { key: "travel", icon: "✈️", emojis: "🚗 🚕 🚙 🚌 🚎 🏎️ 🚓 🚑 🚒 🚐 🚚 🚛 🚜 🛵 🏍️ 🚲 ✈️ 🚀 🛸 🚁 ⛵ 🚤 🚢 🗺️ 🗽 🗼 🏰 🏯 🎡 🎢 🎠 ⛱️ 🏖️ 🏝️ 🏔️ ⛰️ 🌋 🏕️ 🌅 🌄 🌠 🎇 🌈 ☀️ 🌙 ⭐ ☁️ ⛅ ⛈️ ❄️ ⛄".split(" ") },
  { key: "objects", icon: "💡", emojis: "⌚ 📱 💻 ⌨️ 🖥️ 🖨️ 📷 📸 🎥 📺 🔋 🔌 💡 🔦 📡 💰 💵 💳 💎 🔧 🔨 🛠️ 🔑 🔒 🔓 📦 📫 ✉️ 📧 📝 ✏️ 📌 📍 📎 📅 📆 📊 📈 📉 🔍 🔔 🎁 🎈 🎀 🛒 🧾 💊".split(" ") },
  { key: "symbols", icon: "✅", emojis: "✅ ❌ ⭕ ❗ ❓ ‼️ ⚠️ 🚫 ♻️ ✔️ ☑️ 🔆 ⏰ ⏳ ➕ ➖ ➗ ✖️ 💲 💱 ©️ ®️ ™️ 🔔 🔕 🔊 🔇 🆗 🆕 🆒 🔥 💯 💢 💬 💭 🗯️ ♥️ ♦️ ♣️ ♠️ 🔴 🟠 🟡 🟢 🔵 🟣 ⚫ ⚪".split(" ") },
];

export function EmojiPicker({ onPick, onClose, position }) {
  const [cat, setCat] = useState(0);
  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 1100 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ position: "fixed", width: 300, ...position,
                    background: T_NEW.paper, border: `1px solid ${T_NEW.ink12}`,
                    borderRadius: T_NEW.r2, boxShadow: T_NEW.shadowCard, overflow: "hidden",
                    display: "flex", flexDirection: "column" }}>
        {/* Category tabs */}
        <div style={{ display: "flex", overflowX: "auto", borderBottom: `1px solid ${T_NEW.ink12}`,
                      padding: "4px", gap: 2, scrollbarWidth: "none" }}>
          {CATS.map((ct, i) => (
            <button key={ct.key} onClick={() => setCat(i)}
                    style={{ background: i === cat ? T_NEW.persimmonSoft : "transparent",
                             border: "none", borderRadius: T_NEW.r1, cursor: "pointer",
                             fontSize: 18, lineHeight: 1, padding: "5px 6px", flexShrink: 0 }}>
              {ct.icon}
            </button>
          ))}
        </div>
        {/* Emoji grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)",
                      gap: 2, padding: 6, maxHeight: 232, overflowY: "auto" }}>
          {CATS[cat].emojis.map((e, i) => (
            <button key={cat + "-" + i} onClick={() => onPick(e)}
                    style={{ background: "none", border: "none", cursor: "pointer",
                             fontSize: 22, lineHeight: 1.2, padding: 2, borderRadius: 6 }}
                    onMouseEnter={(ev) => ev.currentTarget.style.background = T_NEW.paperSoft}
                    onMouseLeave={(ev) => ev.currentTarget.style.background = "none"}>
              {e}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
